import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Checkbox,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AppSelect from 'components/AppSelect';
import apiReq from 'core/api';
import type {
  PromptTemplateCase,
  PromptTemplateCaseListing,
  PromptTemplateCaseSaveForm,
} from '../../api';
import type { Role } from 'types';

interface CaseFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: PromptTemplateCaseSaveForm) => Promise<void>;
  caseItem?: PromptTemplateCase | PromptTemplateCaseListing | null;
}

export default function CaseFormModal({ open, onClose, onSubmit, caseItem }: CaseFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(caseItem?.id);

  useEffect(() => {
    if (open) {
      setTitle(caseItem?.title ?? '');
      setDescription(caseItem?.description ?? '');
      const full = caseItem as PromptTemplateCase | undefined;
      setRoleIds(full?.role_ids ?? []);
      setError(null);
    }
  }, [open, caseItem]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setRolesLoading(true);
    apiReq<Role[]>('roles/all')
      .then((data) => {
        if (alive) setRoles(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (alive) setRoles([]);
      })
      .finally(() => {
        if (alive) setRolesLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError('Укажите название кейса');
      return;
    }
    if (roleIds.length === 0) {
      setError('Выберите хотя бы одну роль');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        role_ids: roleIds,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  }, [title, description, roleIds, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [loading, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Редактировать кейс' : 'Создать кейс шаблонов'}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, overflow: 'visible' }}
      >
        <TextField
          label="Название"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          fullWidth
          autoFocus
        />
        <TextField
          label="Описание (необязательно)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
        <AppSelect<Role, true>
          multiple
          disableCloseOnSelect
          options={roles}
          value={roles.filter((r) => roleIds.includes(String(r.id)))}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          onChange={(_, newValue) => setRoleIds((newValue as Role[]).map((r) => String(r.id!)))}
          getOptionLabel={(role) => role.title || role.name}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            if (!q) return options;
            return (options as Role[]).filter(
              (r) =>
                (r.title || '').toLowerCase().includes(q) ||
                (r.name || '').toLowerCase().includes(q)
            );
          }}
          isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
          disabled={rolesLoading}
          noOptionsText={rolesLoading ? 'Загрузка…' : 'Нет доступных ролей'}
          limitTags={4}
          renderOption={(props, role, { selected }) => (
            <li {...props} key={(role as Role).id}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox checked={selected} size="small" />
              </ListItemIcon>
              <ListItemText primary={(role as Role).title || (role as Role).name} />
            </li>
          )}
          label="Роли"
          textFieldProps={{
            placeholder:
              roleIds.length === 0 ? 'Введите для поиска или выберите из списка…' : 'Добавить ещё…',
            helperText: 'Выберите роли, для которых будет доступен этот кейс',
            required: true,
          }}
          renderTags={(value, getTagProps) =>
            (value as Role[]).map((role, index) => (
              <Chip
                {...getTagProps({ index })}
                key={role.id}
                label={role.title || role.name}
                size="small"
                variant="outlined"
              />
            ))
          }
        />
        {error && <Box sx={{ color: 'error.main', fontSize: 14 }}>{error}</Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Отмена
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : isEdit ? 'Сохранить' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
